import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { DragDropContext } from '@hello-pangea/dnd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { InteracoesTimeline } from '../components/InteracoesTimeline';
import {
  Plus, Search, DollarSign, Users, Calendar, Phone, Mail,
  MoreHorizontal, FileText, Target, CheckCircle2, Loader2,
  ArrowRight, BarChart3, Ship, X, FolderPlus,
  User, AlertTriangle, Gavel, Paperclip, Upload,
  Download, Trash2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { TitleWithHelp } from '../components/ui/help-tooltip';
import { CurrencyInput } from '../components/ui/currency-input';

import { API, getAuthHeader, formatCurrency, formatDate } from '../components/CRM/crmUtils';
import { ColunaPipeline } from '../components/CRM/Pipeline/ColunaPipeline';
import {
  DescarteDialog, ConcluidoDialog, CriarProjetoDialog, DeleteDialog, NovaTarefaDialog
} from '../components/CRM/Pipeline/ActionDialogs';
import { CriarProjetoFullDialog } from '../components/CRM/Pipeline/CriarProjetoFullDialog';
import { DetalhesDialog } from '../components/CRM/Pipeline/DetalhesDialog';
import { MODALIDADES_LICITACAO, ORIGENS_PRIORIDADE_ALTA, emptyForm } from '../components/CRM/Pipeline/constants';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';

const CrmPipeline = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [etapas, setEtapas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOportunidade, setEditingOportunidade] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');

  const [origens, setOrigens] = useState([]);
  const [motivosDescarte, setMotivosDescarte] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [criteriosScoring, setCriteriosScoring] = useState([]);

  const [usuarios, setUsuarios] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [fabricas, setFabricas] = useState([]);
  const [codigosPaises, setCodigosPaises] = useState([]);
  const [produtosCatalogo, setProdutosCatalogo] = useState([]);

  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [searchingCliente, setSearchingCliente] = useState(false);

  const [indicadorSearchTerm, setIndicadorSearchTerm] = useState('');
  const [indicadorSearchResults, setIndicadorSearchResults] = useState([]);
  const [searchingIndicador, setSearchingIndicador] = useState(false);

  const [showDescarteDialog, setShowDescarteDialog] = useState(false);
  const [descarteData, setDescarteData] = useState({ id: '', motivo: '', detalhe: '' });
  const [showConcluidoDialog, setShowConcluidoDialog] = useState(false);
  const [concluidoData, setConcluidoData] = useState({ id: '', valor_final: 0, criar_projeto: false });
  const [deleteOportunidadeId, setDeleteOportunidadeId] = useState(null);

  const [showCriarProjetoDialog, setShowCriarProjetoDialog] = useState(false);
  const [oportunidadeParaProjeto, setOportunidadeParaProjeto] = useState(null);
  const [propostaParaProjeto, setPropostaParaProjeto] = useState(null);
  const [criarProjetoLoading, setCriarProjetoLoading] = useState(false);

  const [showDetalhes, setShowDetalhes] = useState(false);
  const [oportunidadeDetalhes, setOportunidadeDetalhes] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterResponsavel, setFilterResponsavel] = useState('todos');
  const [filterOrigem, setFilterOrigem] = useState('todos');
  const [filterPrioridade, setFilterPrioridade] = useState('todos');
  const [filterValorMin, setFilterValorMin] = useState('');
  const [filterValorMax, setFilterValorMax] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [showTarefaDialog, setShowTarefaDialog] = useState(false);
  const [tarefaForm, setTarefaForm] = useState({ titulo: '', descricao: '', tipo: 'follow_up', prioridade: 'media', data_vencimento: '', responsavel_id: '', responsavel_nome: '', oportunidade_id: '', oportunidade_titulo: '' });
  const [savingTarefa, setSavingTarefa] = useState(false);

  const openNovaTarefa = (opp, prefilledTitle = '', dataVencimento = null) => {
    setTarefaForm({
      titulo: prefilledTitle || `Follow-up: ${opp?.titulo || ''}`,
      descricao: '',
      tipo: 'follow_up',
      prioridade: 'media',
      data_vencimento: dataVencimento ? dataVencimento.slice(0, 10) : new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      responsavel_id: opp?.responsavel_id || '',
      responsavel_nome: opp?.responsavel_nome || '',
      oportunidade_id: opp?.id || '',
      oportunidade_titulo: opp?.titulo || '',
    });
    setShowTarefaDialog(true);
  };

  const handleSaveTarefa = async () => {
    if (!tarefaForm.titulo.trim()) { toast.error('Título é obrigatório'); return; }
    setSavingTarefa(true);
    try {
      await api.post('/crm-tarefas', tarefaForm);
      toast.success('Tarefa criada com sucesso');
      setShowTarefaDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar tarefa');
    } finally { setSavingTarefa(false); }
  };

  // ============ FETCH DATA ============
  useEffect(() => {
    fetchConfig(); fetchPipeline(); fetchStats();
    fetchUsuarios(); fetchModelos(); fetchFabricas();
    fetchCodigosPaises(); fetchProdutos();
  }, []);

  const fetchConfig = async () => {
    try {
      const [etapasRes, origensRes, motivosRes, prioridadesRes, scoringRes] = await Promise.all([
        api.get('/crm-oportunidades/config/etapas'),
        api.get('/crm-oportunidades/config/origens'),
        api.get('/crm-oportunidades/config/motivos-descarte'),
        api.get('/crm-oportunidades/config/prioridades'),
        api.get('/crm-oportunidades/config/lead-scoring-criterios'),
      ]);
      setEtapas(etapasRes.data); setOrigens(origensRes.data);
      setMotivosDescarte(motivosRes.data); setPrioridades(prioridadesRes.data);
      setCriteriosScoring(scoringRes.data.criterios || []);
    } catch (err) { console.error('Erro ao carregar configurações:', err); }
  };

  const fetchCodigosPaises = async () => {
    try {
      const res = await api.get('/crm-oportunidades/config/codigos-paises');
      setCodigosPaises(res.data);
    } catch {
      setCodigosPaises([
        { codigo: '+55', pais: 'Brasil', bandeira: '🇧🇷' },
        { codigo: '+1', pais: 'EUA', bandeira: '🇺🇸' },
        { codigo: '+351', pais: 'Portugal', bandeira: '🇵🇹' },
      ]);
    }
  };

  const fetchProdutos = async () => {
    try { const res = await api.get('/produtos'); setProdutosCatalogo(res.data); }
    catch (err) { console.error('Erro ao carregar produtos:', err); }
  };

  const fetchPipeline = async () => {
    try { const res = await api.get('/crm-oportunidades/pipeline'); setPipeline(res.data); }
    catch { toast.error('Erro ao carregar pipeline'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const res = await api.get('/crm-oportunidades/stats'); setStats(res.data); }
    catch (err) { console.error('Erro ao carregar estatísticas:', err); }
  };

  const fetchUsuarios = async () => {
    try { const res = await api.get('/users/simple'); setUsuarios(res.data.filter(u => u.is_active)); }
    catch (err) { console.error('Erro ao carregar usuários:', err); }
  };

  const fetchModelos = async () => {
    try { const res = await api.get('/vessel-models'); setModelos(res.data.filter(m => m.is_active)); }
    catch (err) { console.error('Erro ao carregar modelos:', err); }
  };

  const fetchFabricas = async () => {
    try { const res = await api.get('/factories'); setFabricas(res.data); }
    catch (err) { console.error('Erro ao carregar fábricas:', err); }
  };

  // ============ CLIENT/INDICATOR SEARCH ============
  const buscarClientes = useCallback(async (termo) => {
    if (!termo || termo.length < 2) { setClienteSearchResults([]); return; }
    setSearchingCliente(true);
    try { const res = await api.get(`/crm-oportunidades/buscar-clientes?q=${encodeURIComponent(termo)}`); setClienteSearchResults(res.data); }
    catch { } finally { setSearchingCliente(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => buscarClientes(clienteSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [clienteSearchTerm, buscarClientes]);

  const buscarIndicador = useCallback(async (termo) => {
    if (!termo || termo.length < 2) { setIndicadorSearchResults([]); return; }
    setSearchingIndicador(true);
    try { const res = await api.get(`/crm-oportunidades/buscar-clientes?q=${encodeURIComponent(termo)}`); setIndicadorSearchResults(res.data); }
    catch { } finally { setSearchingIndicador(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => buscarIndicador(indicadorSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [indicadorSearchTerm, buscarIndicador]);

  // ============ SCORING ============
  const calcularProbabilidade = (scoring) => {
    const pesos = { orcamento_confirmado: 20, decisor_identificado: 20, necessidade_clara: 20, prazo_definido: 20, sem_concorrencia: 20 };
    return Object.entries(scoring || {}).reduce((acc, [key, value]) => acc + (value ? (pesos[key] || 0) : 0), 0);
  };

  const handleScoringChange = (criterio, checked) => {
    const newScoring = { ...form.lead_scoring, [criterio]: checked };
    setForm(prev => ({ ...prev, lead_scoring: newScoring, probabilidade: calcularProbabilidade(newScoring) }));
  };

  // ============ AUTO TITLE ============
  const gerarTituloAutomatico = useCallback(() => {
    const partes = [];
    if (form.cliente_nome) partes.push(form.cliente_nome);
    else if (form.contato_nome) partes.push(form.contato_nome);
    const origemNome = origens.find(o => o.id === form.origem)?.nome;
    if (origemNome && form.origem !== 'outro') partes.push(origemNome);
    if (form.embarcacoes?.length > 0) {
      const totalEmb = form.embarcacoes.reduce((acc, e) => acc + (e.quantidade || 1), 0);
      const primeiroModelo = form.embarcacoes[0]?.modelo_nome;
      if (primeiroModelo) partes.push(`${totalEmb}x ${primeiroModelo}`);
    }
    if (form.produtos?.length > 0 && form.embarcacoes?.length === 0) {
      const totalProd = form.produtos.reduce((acc, p) => acc + (p.quantidade || 1), 0);
      const primeiroProd = form.produtos[0]?.produto_nome;
      if (primeiroProd) partes.push(`${totalProd}x ${primeiroProd}`);
    }
    return partes.join(' - ');
  }, [form.cliente_nome, form.contato_nome, form.origem, form.embarcacoes, form.produtos, origens]);

  useEffect(() => {
    if (!editingOportunidade && !form.titulo) {
      const tituloSugerido = gerarTituloAutomatico();
      if (tituloSugerido) setForm(prev => ({ ...prev, titulo: tituloSugerido }));
    }
  }, [form.cliente_nome, form.contato_nome, form.embarcacoes, form.produtos, gerarTituloAutomatico, editingOportunidade]);

  const handleOrigemChange = (novaOrigem) => {
    const updates = { origem: novaOrigem };
    if (ORIGENS_PRIORIDADE_ALTA.includes(novaOrigem)) updates.prioridade = 'alta';
    setForm(prev => ({ ...prev, ...updates }));
  };

  // ============ ATTACHMENTS ============
  const handleUploadAnexo = async (file) => {
    if (!editingOportunidade) { toast.error('Salve a oportunidade antes de anexar arquivos'); return null; }
    setUploadingAnexo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // IMPORTANTE: não setar Content-Type manualmente (browser gera boundary correto para FormData)
      const res = await axios.post(
        `${API}/crm-oportunidades/${editingOportunidade.id}/anexos`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` } }
      );
      setForm(prev => ({ ...prev, anexos: [...(prev.anexos || []), res.data] }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar anexo');
      throw err;
    }
    finally { setUploadingAnexo(false); }
  };

  const handleDeleteAnexo = async (anexoId) => {
    if (!editingOportunidade) return;
    try {
      await api.delete(`/crm-oportunidades/${editingOportunidade.id}/anexos/${anexoId}`);
      toast.success('Anexo excluído');
      setForm(prev => ({ ...prev, anexos: (prev.anexos || []).filter(a => a.id !== anexoId) }));
    } catch { toast.error('Erro ao excluir anexo'); }
  };

  const getStableFileUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('/api/')) return url;
    if (url.includes('amazonaws.com') || url.includes('s3.') || url.startsWith('s3://')) {
      const filename = url.split('?')[0].split('/').pop();
      return `/api/anexos/${filename}`;
    }
    if (url.includes('/api/anexos/') || url.includes('/api/images/')) {
      const match = url.match(/\/api\/(anexos|images)\/(.+)/);
      if (match) return `/api/${match[1]}/${match[2]}`;
    }
    if (url.startsWith('http')) return url;
    return `/api/anexos/${url}`;
  };

  const handleDownloadAnexo = async (anexo) => {
    if (!editingOportunidade) { window.open(getStableFileUrl(anexo.url), '_blank'); return; }
    try {
      const res = await api.get(`/crm-oportunidades/${editingOportunidade.id}/anexos/${anexo.id}/download`);
      window.open(getStableFileUrl(res.data.url), '_blank');
    } catch { window.open(getStableFileUrl(anexo.url), '_blank'); }
  };

  // ============ DRAG AND DROP ============
  const onDragEnd = useCallback(async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const oportunidadeId = draggableId;
    const novaEtapa = destination.droppableId;
    const etapaAntiga = source.droppableId;
    if (novaEtapa === 'descartado') {
      setDescarteData({ id: oportunidadeId, motivo: '', detalhe: '' });
      setShowDescarteDialog(true);
      return;
    }
    if (novaEtapa === 'concluido') {
      const opp = pipeline[etapaAntiga]?.oportunidades.find(o => o.id === oportunidadeId);
      setConcluidoData({ id: oportunidadeId, valor_final: opp?.valor_estimado || 0, criar_projeto: false, oportunidade: opp });
      setShowConcluidoDialog(true);
      return;
    }
    await moverOportunidade(oportunidadeId, novaEtapa);
  }, [pipeline]);

  const moverOportunidade = async (id, novaEtapa, extras = {}) => {
    try {
      await api.put(`/crm-oportunidades/${id}/mover`, { nova_etapa: novaEtapa, ...extras });
      toast.success('Oportunidade movida');
      fetchPipeline(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao mover oportunidade'); }
  };

  const handleDescartar = async () => {
    if (!descarteData.motivo) { toast.error('Selecione o motivo do descarte'); return; }
    await moverOportunidade(descarteData.id, 'descartado', { motivo_descarte: descarteData.motivo, motivo_descarte_detalhe: descarteData.detalhe });
    setShowDescarteDialog(false);
    setDescarteData({ id: '', motivo: '', detalhe: '' });
  };

  const handleConcluir = async () => {
    await moverOportunidade(concluidoData.id, 'concluido', { valor_final: concluidoData.valor_final });
    setShowConcluidoDialog(false);
    if (concluidoData.criar_projeto && concluidoData.oportunidade) {
      const opp = concluidoData.oportunidade;
      setOportunidadeParaProjeto(opp);
      // Buscar proposta vinculada à oportunidade
      try {
        const res = await api.get('/propostas');
        const propostas = res.data || [];
        const proposta = propostas.find(p => p.oportunidade_id === opp.id && p.status === 'aceita')
          || propostas.find(p => p.oportunidade_id === opp.id);
        setPropostaParaProjeto(proposta || null);
      } catch { setPropostaParaProjeto(null); }
      setShowCriarProjetoDialog(true);
    }
    setConcluidoData({ id: '', valor_final: 0, criar_projeto: false });
  };

  const handleCriarProjeto = async (formData) => {
    if (!formData.name) { toast.error('Nome do projeto é obrigatório'); return; }
    setCriarProjetoLoading(true);
    try {
      await api.post(`/crm-oportunidades/${oportunidadeParaProjeto.id}/criar-projeto`, {
        oportunidade_id: oportunidadeParaProjeto.id, ...formData,
      });
      toast.success('Projeto criado com sucesso!');
      setShowCriarProjetoDialog(false); setOportunidadeParaProjeto(null); setPropostaParaProjeto(null); fetchPipeline();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar projeto'); }
    finally { setCriarProjetoLoading(false); }
  };

  // ============ CRUD ============
  const handleSubmit = async () => {
    if (!form.titulo.trim()) { toast.error('Título é obrigatório'); return; }
    if (!form.responsavel_id && !form.responsavel_nome) { toast.error('Responsável é obrigatório'); return; }
    setSubmitLoading(true);
    try {
      const payload = { ...form };
      payload.valor_estimado = parseFloat(payload.valor_estimado) || 0;
      payload.probabilidade = calcularProbabilidade(payload.lead_scoring);
      if (editingOportunidade) {
        await api.put(`/crm-oportunidades/${editingOportunidade.id}`, payload);
        toast.success('Oportunidade atualizada');
      } else {
        const res = await api.post('/crm-oportunidades', payload);
        toast.success('Oportunidade criada');
        if (res.data?.id) {
          setTimeout(() => { setOportunidadeDetalhes(res.data); setShowDetalhes(true); }, 300);
        }
      }
      setShowDialog(false); setEditingOportunidade(null); setForm(emptyForm);
      setClienteSearchTerm(''); setIndicadorSearchTerm('');
      fetchPipeline(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSubmitLoading(false); }
  };

  const handleEdit = (oportunidade) => {
    setEditingOportunidade(oportunidade);
    setForm({ ...emptyForm, ...oportunidade, embarcacoes: oportunidade.embarcacoes || [], tags: oportunidade.tags || [], lead_scoring: oportunidade.lead_scoring || emptyForm.lead_scoring });
    setClienteSearchTerm(oportunidade.cliente_nome || '');
    setIndicadorSearchTerm(oportunidade.indicado_por_cliente_nome || '');
    setActiveTab('dados'); setShowDialog(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/crm-oportunidades/${deleteOportunidadeId}`);
      toast.success('Oportunidade excluída'); setDeleteOportunidadeId(null); fetchPipeline(); fetchStats();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleViewDetalhes = async (oportunidade) => {
    setOportunidadeDetalhes(oportunidade); setShowDetalhes(true);
  };

  const refreshDetalhes = async () => {
    if (!oportunidadeDetalhes) return;
    try {
      const res = await api.get(`/crm-oportunidades/${oportunidadeDetalhes.id}/interacoes`);
      setOportunidadeDetalhes(prev => ({ ...prev, interacoes: res.data }));
    } catch { fetchPipeline(); }
  };

  const handleSelectCliente = (cliente) => {
    setForm(prev => ({ ...prev, cliente_id: cliente.id, cliente_nome: cliente.nome, contato_nome: cliente.nome, contato_email: cliente.email || '', contato_telefone: cliente.telefone || '' }));
    setClienteSearchTerm(cliente.nome); setClienteSearchResults([]);
  };

  const handleSelectIndicador = (cliente) => {
    setForm(prev => ({ ...prev, indicado_por_cliente_id: cliente.id, indicado_por_cliente_nome: cliente.nome }));
    setIndicadorSearchTerm(cliente.nome); setIndicadorSearchResults([]);
  };

  const addEmbarcacao = () => {
    setForm(prev => ({ ...prev, embarcacoes: [...prev.embarcacoes, { modelo_id: '', modelo_nome: '', quantidade: 1 }] }));
  };

  const updateEmbarcacao = (idx, field, value) => {
    const embs = [...form.embarcacoes];
    if (field === 'modelo_id') {
      const modelo = modelos.find(m => m.id === value);
      embs[idx] = { ...embs[idx], modelo_id: value, modelo_nome: modelo?.nome || '' };
    } else { embs[idx] = { ...embs[idx], [field]: value }; }
    setForm(prev => ({ ...prev, embarcacoes: embs }));
  };

  const removeEmbarcacao = (idx) => {
    setForm(prev => ({ ...prev, embarcacoes: prev.embarcacoes.filter((_, i) => i !== idx) }));
  };

  // ============ FILTERS ============
  const getFilteredPipeline = useMemo(() => {
    if (!searchTerm && filterResponsavel === 'todos' && filterOrigem === 'todos' && filterPrioridade === 'todos' && !filterValorMin && !filterValorMax) return pipeline;
    const filtered = {};
    Object.keys(pipeline).forEach(etapaId => {
      const etapaData = pipeline[etapaId];
      let opps = etapaData.oportunidades || [];
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        opps = opps.filter(o => o.titulo?.toLowerCase().includes(s) || o.cliente_nome?.toLowerCase().includes(s) || o.contato_nome?.toLowerCase().includes(s) || o.contato_telefone?.includes(searchTerm) || o.licitacao_numero_processo?.toLowerCase().includes(s));
      }
      if (filterResponsavel !== 'todos') opps = opps.filter(o => o.responsavel_id === filterResponsavel);
      if (filterOrigem !== 'todos') opps = opps.filter(o => o.origem === filterOrigem);
      if (filterPrioridade !== 'todos') opps = opps.filter(o => o.prioridade === filterPrioridade);
      if (filterValorMin) opps = opps.filter(o => (o.valor_estimado || 0) >= parseFloat(filterValorMin));
      if (filterValorMax) opps = opps.filter(o => (o.valor_estimado || 0) <= parseFloat(filterValorMax));
      filtered[etapaId] = { ...etapaData, oportunidades: opps };
    });
    return filtered;
  }, [pipeline, searchTerm, filterResponsavel, filterOrigem, filterPrioridade, filterValorMin, filterValorMax]);

  const openNewOportunidade = (etapaId = 'lead') => {
    setEditingOportunidade(null);
    setForm({ ...emptyForm, etapa: etapaId });
    setClienteSearchTerm(''); setIndicadorSearchTerm('');
    setActiveTab('dados'); setShowDialog(true);
  };

  return (
    <div className="space-y-4" data-testid="crm-pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Target className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Pipeline de Vendas</h1>
              <TitleWithHelp
                title="Pipeline"
                description="Gerencie suas oportunidades comerciais através do funil de vendas. Arraste e solte os cards para mover entre as etapas."
                connections={["Clientes — Vincule oportunidades a clientes cadastrados", "Projetos — Crie projetos ao concluir vendas", "WhatsApp — Integração para distribuição de leads (em breve)"]}
                tips={["Use o Lead Scoring para calcular a probabilidade de fechamento", "Preencha o motivo ao descartar uma oportunidade", "Ao concluir, você pode criar um projeto automaticamente"]}
              />
            </div>
            <p className="text-sm text-gray-500">Gerencie oportunidades e acompanhe o funil comercial</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowStats(!showStats)} data-testid="btn-toggle-stats">
            <BarChart3 className="h-4 w-4 mr-1" />
            {showStats ? 'Ocultar' : 'Métricas'}
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => openNewOportunidade()} data-testid="btn-nova-oportunidade">
            <Plus className="mr-2 h-4 w-4" />
            Nova Oportunidade
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {showStats && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-panel">
          <Card><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-purple-600">{stats.totais?.total_oportunidades || 0}</div><div className="text-xs text-gray-500">Total</div></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-green-600">{stats.totais?.concluidas || 0}</div><div className="text-xs text-gray-500">Concluídas</div></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-red-600">{stats.totais?.descartadas || 0}</div><div className="text-xs text-gray-500">Descartadas</div></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-blue-600">{stats.totais?.taxa_conversao || 0}%</div><div className="text-xs text-gray-500">Taxa Conversão</div></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-emerald-600">{formatCurrency(stats.totais?.valor_concluido)}</div><div className="text-xs text-gray-500">Valor Convertido</div></CardContent></Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar por título, cliente, nº processo..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-oportunidades" />
            </div>
            <Select value={filterResponsavel} onValueChange={setFilterResponsavel}>
              <SelectTrigger className="w-[160px]" data-testid="filter-responsavel">
                <User className="h-3.5 w-3.5 mr-1 text-gray-400" /><SelectValue placeholder="Responsável" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos Responsáveis</SelectItem>
                {usuarios.map(u => (<SelectItem key={u.email} value={u.id || u.email}>{u.full_name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              {showFilters ? 'Menos' : 'Mais'} Filtros
            </Button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              <Select value={filterOrigem} onValueChange={setFilterOrigem}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Origem" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas Origens</SelectItem>
                  {origens.map(o => (<SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filterPrioridade} onValueChange={setFilterPrioridade}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Prioridade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas Prioridades</SelectItem>
                  {prioridades.map(p => (<SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Valor mín" value={filterValorMin} onChange={e => setFilterValorMin(e.target.value)} className="w-[120px]" />
              <Input type="number" placeholder="Valor máx" value={filterValorMax} onChange={e => setFilterValorMax(e.target.value)} className="w-[120px]" />
              <Button variant="ghost" size="sm" onClick={() => { setFilterResponsavel('todos'); setFilterOrigem('todos'); setFilterPrioridade('todos'); setFilterValorMin(''); setFilterValorMax(''); setSearchTerm(''); }}>
                Limpar Filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kanban */}
      {loading ? (
        <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {etapas.map(etapa => (
              <ColunaPipeline
                key={etapa.id}
                etapa={etapa}
                dados={getFilteredPipeline[etapa.id] || { oportunidades: [], total_valor: 0, quantidade: 0 }}
                origens={origens}
                onAddNew={openNewOportunidade}
                onView={handleViewDetalhes}
                onEdit={handleEdit}
                onDelete={setDeleteOportunidadeId}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {/* ========== DIALOG CRIAR/EDITAR OPORTUNIDADE ========== */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        if (!open) {
          const hasData = form.titulo || form.cliente_nome || form.valor_estimado;
          if (hasData && !window.confirm('Tem certeza? Os dados não salvos serão perdidos.')) return;
          setShowDialog(false); setEditingOportunidade(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} data-testid="dialog-oportunidade">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              {editingOportunidade ? 'Editar Oportunidade' : 'Nova Oportunidade'}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="cliente">Cliente</TabsTrigger>
              <TabsTrigger value="scoring">Scoring</TabsTrigger>
              <TabsTrigger value="anexos">Anexos</TabsTrigger>
            </TabsList>

            {/* Aba Dados */}
            <TabsContent value="dados" className="space-y-3 mt-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Título *</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-purple-600"
                    onClick={() => { const titulo = gerarTituloAutomatico(); if (titulo) setForm(p => ({ ...p, titulo })); }}>
                    <Sparkles className="h-3 w-3 mr-1" /> Gerar
                  </Button>
                </div>
                <Input value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                  placeholder="Cliente - Origem - Qtd - Modelo (gerado automaticamente)" data-testid="input-titulo" />
                <p className="text-[10px] text-gray-400 mt-0.5">Preencha cliente e embarcações para gerar título automático</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Etapa</Label>
                  <Select value={form.etapa} onValueChange={v => setForm(p => ({ ...p, etapa: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {etapas.filter(e => e.id !== 'concluido' && e.id !== 'descartado').map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Prioridade</Label>
                  <Select value={form.prioridade} onValueChange={v => setForm(p => ({ ...p, prioridade: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {prioridades.map(p => (<SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor Estimado (R$)</Label>
                  <CurrencyInput value={form.valor_estimado} onChange={v => setForm(p => ({ ...p, valor_estimado: v }))} data-testid="input-valor" />
                </div>
                <div>
                  <Label className="text-xs">Responsável *</Label>
                  <Select value={(() => {
                    const match = usuarios.find(u => u.id === form.responsavel_id || u.email === form.responsavel_id);
                    return match?.email || '';
                  })()} onValueChange={v => {
                    const user = usuarios.find(u => u.email === v);
                    setForm(p => ({ ...p, responsavel_id: user?.id || v, responsavel_nome: user?.full_name || '' }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Selecionar responsável..." /></SelectTrigger>
                    <SelectContent>
                      {usuarios.map(u => (<SelectItem key={u.email} value={u.email}>{u.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Origem {ORIGENS_PRIORIDADE_ALTA.includes(form.origem) && <Badge className="ml-1 text-[9px] bg-red-100 text-red-600">Prioridade Alta</Badge>}</Label>
                  <Select value={form.origem} onValueChange={handleOrigemChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{origens.map(o => (<SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Previsão de Fechamento</Label>
                  <Input type="date" value={form.data_previsao_fechamento} onChange={e => setForm(p => ({ ...p, data_previsao_fechamento: e.target.value }))} />
                </div>
              </div>

              {form.origem === 'indicacao' && (
                <div className="border rounded-lg p-3 bg-blue-50/50">
                  <Label className="text-xs font-medium text-blue-700 mb-2 block">Dados da Indicação</Label>
                  <div>
                    <Label className="text-xs">Quem indicou?</Label>
                    <div className="relative">
                      <Input value={indicadorSearchTerm} onChange={e => { setIndicadorSearchTerm(e.target.value); if (!e.target.value) setForm(p => ({ ...p, indicado_por_cliente_id: '', indicado_por_cliente_nome: '' })); }}
                        placeholder="Digite nome, telefone ou email para buscar..." />
                      {searchingIndicador && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin" />}
                      {indicadorSearchResults.length > 0 && (
                        <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                          {indicadorSearchResults.map(c => (
                            <div key={c.id} className="p-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => handleSelectIndicador(c)}>
                              <div className="font-medium">{c.nome}</div>
                              <div className="text-xs text-gray-500">{c.telefone || c.email || ''}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {form.origem === 'licitacao' && (
                <div className="border rounded-lg p-3 bg-amber-50/50">
                  <Label className="text-xs font-medium text-amber-700 mb-2 block flex items-center gap-1">
                    <Gavel className="h-3.5 w-3.5" /> Dados da Licitação
                  </Label>
                  <p className="text-[10px] text-amber-600 mb-2">O órgão/entidade será obtido automaticamente do cadastro do cliente</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Número do Processo</Label>
                      <Input value={form.licitacao_numero_processo} onChange={e => setForm(p => ({ ...p, licitacao_numero_processo: e.target.value }))} placeholder="Ex: PE 001/2025" />
                    </div>
                    <div>
                      <Label className="text-xs">Modalidade</Label>
                      <Select value={form.licitacao_modalidade || 'none'} onValueChange={v => setForm(p => ({ ...p, licitacao_modalidade: v === 'none' ? '' : v }))}>
                        <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Selecionar</SelectItem>
                          {MODALIDADES_LICITACAO.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Data Abertura</Label>
                      <Input type="date" value={form.licitacao_data_abertura} onChange={e => setForm(p => ({ ...p, licitacao_data_abertura: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Data Encerramento</Label>
                      <Input type="date" value={form.licitacao_data_encerramento} onChange={e => setForm(p => ({ ...p, licitacao_data_encerramento: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Observações</Label>
                <Textarea value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))} rows={2} />
              </div>
            </TabsContent>

            {/* Aba Cliente */}
            <TabsContent value="cliente" className="space-y-3 mt-3">
              <div>
                <Label className="text-xs font-medium">Buscar Cliente Cadastrado</Label>
                <p className="text-xs text-gray-500 mb-2">Digite nome, telefone, CPF, CNPJ ou email</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input value={clienteSearchTerm} onChange={e => { setClienteSearchTerm(e.target.value); if (!e.target.value) setForm(p => ({ ...p, cliente_id: '', cliente_nome: '' })); }}
                    placeholder="Digite para buscar..." className="pl-9" data-testid="input-busca-cliente" />
                  {searchingCliente && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin" />}
                </div>
                {clienteSearchResults.length > 0 && (
                  <div className="border rounded-lg mt-2 max-h-48 overflow-y-auto">
                    {clienteSearchResults.map(c => (
                      <div key={c.id} className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0" onClick={() => handleSelectCliente(c)}>
                        <div className="font-medium text-sm">{c.nome}</div>
                        <div className="text-xs text-gray-500 flex gap-3">
                          {c.telefone && <span>{c.telefone}</span>}
                          {c.email && <span>{c.email}</span>}
                          {c.cnpj && <span>CNPJ: {c.cnpj}</span>}
                          {c.cpf && <span>CPF: {c.cpf}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {form.cliente_id && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div><span className="text-xs text-green-600">Cliente vinculado:</span><span className="ml-2 font-medium text-sm">{form.cliente_nome}</span></div>
                    <Button variant="ghost" size="sm" onClick={() => { setForm(p => ({ ...p, cliente_id: '', cliente_nome: '' })); setClienteSearchTerm(''); }}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-gray-500 mb-3">Ou preencha os dados de contato manualmente:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nome do Contato</Label><Input value={form.contato_nome} onChange={e => setForm(p => ({ ...p, contato_nome: e.target.value }))} /></div>
                  <div><Label className="text-xs">E-mail</Label><Input value={form.contato_email} onChange={e => setForm(p => ({ ...p, contato_email: e.target.value }))} /></div>
                  <div>
                    <Label className="text-xs">Telefone</Label>
                    <div className="flex gap-1">
                      <Select value={form.contato_codigo_pais || '+55'} onValueChange={v => setForm(p => ({ ...p, contato_codigo_pais: v }))}>
                        <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{codigosPaises.map(p => (<SelectItem key={p.codigo} value={p.codigo}>{p.bandeira} {p.codigo}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input className="flex-1" value={form.contato_telefone} onChange={e => setForm(p => ({ ...p, contato_telefone: e.target.value }))} placeholder="(00) 00000-0000" />
                    </div>
                  </div>
                  <div><Label className="text-xs">WhatsApp</Label><Input value={form.contato_whatsapp} onChange={e => setForm(p => ({ ...p, contato_whatsapp: e.target.value }))} placeholder="(00) 00000-0000" /></div>
                </div>
              </div>
            </TabsContent>

            {/* Aba Lead Scoring */}
            <TabsContent value="scoring" className="space-y-4 mt-3">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <h4 className="font-medium text-purple-700 text-sm mb-1">Como funciona o Lead Scoring?</h4>
                <p className="text-xs text-purple-600">Marque os critérios que se aplicam. Cada critério vale 20%. A probabilidade é calculada automaticamente.</p>
              </div>
              <div className="space-y-3">
                {criteriosScoring.map(criterio => (
                  <div key={criterio.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50">
                    <Checkbox id={criterio.id} checked={form.lead_scoring?.[criterio.id] || false} onCheckedChange={checked => handleScoringChange(criterio.id, checked)} />
                    <div className="flex-1">
                      <Label htmlFor={criterio.id} className="font-medium text-sm cursor-pointer">
                        {criterio.nome}<Badge variant="outline" className="ml-2 text-[10px]">+{criterio.peso}%</Badge>
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">{criterio.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-100 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">{form.probabilidade || 0}%</div>
                <div className="text-sm text-gray-600">Probabilidade de Fechamento</div>
                <div className="text-xs text-gray-500 mt-1">Valor Ponderado: {formatCurrency((form.valor_estimado || 0) * (form.probabilidade || 0) / 100)}</div>
              </div>
            </TabsContent>

            {/* Aba Anexos - padronizado com AnexosManager */}
            <TabsContent value="anexos" className="space-y-3 mt-3">
              {!editingOportunidade ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                  <AlertTriangle className="h-4 w-4 inline mr-1" /> Salve a oportunidade primeiro para poder anexar arquivos
                </div>
              ) : (
              <AnexosErrorBoundary>
                <AnexosManager
                  anexos={(form.anexos || []).map(a => ({
                    id: a.id,
                    nome: a.nome || a.filename,
                    nome_exibicao: a.nome_exibicao || (a.nome || a.filename || '').replace(/\.[^/.]+$/, ''),
                    url: a.url || a.download_url,
                    tipo: a.tipo || a.content_type,
                    tamanho: a.tamanho || a.size,
                    data_upload: a.uploaded_at || a.data_upload,
                  }))}
                  onChange={(novos) => setForm(prev => ({ ...prev, anexos: novos }))}
                  maxFiles={30}
                  maxSizeMB={50}
                  label="Anexos (Documentos, propostas, contratos)"
                  modulo="crm_oportunidades"
                  entidadeId={editingOportunidade.id}
                  uploadHandler={async (file) => {
                    const novoAnexo = await handleUploadAnexo(file);
                    if (!novoAnexo) return null;
                    return {
                      id: novoAnexo.id,
                      nome: novoAnexo.nome || novoAnexo.filename,
                      nome_exibicao: (novoAnexo.nome || novoAnexo.filename || '').replace(/\.[^/.]+$/, ''),
                      url: novoAnexo.url,
                      tipo: novoAnexo.tipo || novoAnexo.content_type,
                      tamanho: novoAnexo.tamanho || novoAnexo.size,
                      data_upload: novoAnexo.uploaded_at || new Date().toISOString(),
                    };
                  }}
                  deleteHandler={async (anexo) => {
                    await handleDeleteAnexo(anexo.id);
                  }}
                />
              </AnexosErrorBoundary>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              const hasData = form.titulo || form.cliente_nome || form.valor_estimado;
              if (hasData && !window.confirm('Tem certeza? Os dados não salvos serão perdidos.')) return;
              setShowDialog(false); setEditingOportunidade(null);
            }}>Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleSubmit} disabled={submitLoading} data-testid="btn-salvar-oportunidade">
              {submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingOportunidade ? 'Atualizar' : 'Criar Oportunidade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== ACTION DIALOGS ========== */}
      <DescarteDialog open={showDescarteDialog} onOpenChange={setShowDescarteDialog} descarteData={descarteData} setDescarteData={setDescarteData} motivosDescarte={motivosDescarte} onConfirm={handleDescartar} />
      <ConcluidoDialog open={showConcluidoDialog} onOpenChange={setShowConcluidoDialog} concluidoData={concluidoData} setConcluidoData={setConcluidoData} onConfirm={handleConcluir} />
      <CriarProjetoFullDialog
        open={showCriarProjetoDialog} onOpenChange={setShowCriarProjetoDialog}
        oportunidade={oportunidadeParaProjeto} fabricas={fabricas} vesselModels={modelos}
        propostaVinculada={propostaParaProjeto} loading={criarProjetoLoading}
        onConfirm={handleCriarProjeto}
      />
      <DeleteDialog deleteId={deleteOportunidadeId} onOpenChange={setDeleteOportunidadeId} onConfirm={handleDelete} />
      <NovaTarefaDialog open={showTarefaDialog} onOpenChange={setShowTarefaDialog} tarefaForm={tarefaForm} setTarefaForm={setTarefaForm} usuarios={usuarios} saving={savingTarefa} onSave={handleSaveTarefa} />

      {/* ========== DETALHES DIALOG ========== */}
      <DetalhesDialog
        open={showDetalhes}
        onOpenChange={setShowDetalhes}
        oportunidade={oportunidadeDetalhes}
        origens={origens}
        etapas={etapas}
        onNavigatePropostas={(id) => navigate(`/crm/propostas?oportunidade=${id}`)}
        onNovaTarefa={(opp, titulo, dataVencimento) => openNovaTarefa(opp, titulo, dataVencimento)}
        onEdit={handleEdit}
        onRefresh={refreshDetalhes}
      />
    </div>
  );
};

export default CrmPipeline;
